import React, {
  useEffect, useRef, useState,
} from 'react';
import {
  Box, HStack, VStack, Heading, Text, ScrollView, Pressable, Icon, View,
} from '@gluestack-ui/themed';
import {
  ArrowLeft, PanelLeftClose, PanelLeft,
} from 'lucide-react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { navigateToModuleScreen } from '@/common/navigation/RoleNavigations/Student/navigateToModuleScreen';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import { useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';

import ResponseComposer from '../ResponseComposer';
import ResourceSidebar from '../ResourceSidebar';
import AssistanceDrawer from '../components/AssistanceDrawer';
import TaskDescription from '../components/Instructions/TaskDescription';
import TaskObjectives, { hasApplicableObjectives } from '../components/Instructions/TaskObjectives';
import { TaskRequirements } from '../../module/components/TaskRequirements';
import { useSelectedModule } from '../../../common/hooks/student/useSelectedModule';
import { getColorForModule } from '../../../common/utils/getColorForModule';
import { Task, Resource, TaskTypeEnum } from '../types';
import { useTaskSubmissionState } from '../stores/TaskSubmissionStore';
// eslint-disable-next-line import/no-named-as-default
import useTaskSubmissionController from '../hooks/useTaskSubmissionController';
import OralExamPrepWebLayout from './OralExamPrepWebLayout';

interface TaskScreenWebLayoutProps {
  task: Task;
  resources: Resource[];
  thumbnails: Record<string, string | undefined>;
  testID?: string;
  courseId: string;
  moduleId: string;
  readonly?: boolean;
  isLoading: boolean;
  safetyFuseConflict: boolean;
}

// Color map removed in favor of dynamic module tokens
const TaskScreenWebEssayLayout: React.FC<TaskScreenWebLayoutProps> = ({
  task,
  resources,
  thumbnails,
  testID,
  courseId,
  moduleId,
  readonly = false,
  isLoading,
  safetyFuseConflict,
}) => {
  const navigation = useNavigation<NativeStackNavigationProp<StudentRouteParamList>>();
  const { t } = useTranslation();
  const { module, tasks } = useSelectedModule();
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);

  const taskScope = { courseId, moduleId, taskId: task.id };
  const {
    isEvaluating,
    evaluatedAt,
    evaluationError,
  } = useTaskSubmissionState();
  const { submit, evaluate, cancelEvaluation } = useTaskSubmissionController({
    taskScope,
    taskType: task?.type as TaskTypeEnum,
    formalReq: task?.evaluation?.formalCriteria,
    enableAutosave: false, // critical: set to false for all instances except the primary editor (EssayComposer)
  });

  // AI Tutor: show by default if enabled for this task; folded away while an essay's opening page shows.
  const hasAiTutor = task?.evaluation?.mode === 'auto';
  const [isAiCollapsed, setIsAiCollapsed] = useState(false);
  const showAiDrawer = hasAiTutor && !isAiCollapsed;

  const prevIsEvaluatingRef = useRef(isEvaluating);
  const lastChoreographedEvaluatedAtRef = useRef<number | undefined>(undefined);
  const isInitialMountRef = useRef(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    prevIsEvaluatingRef.current = false;
    isInitialMountRef.current = true;
    lastChoreographedEvaluatedAtRef.current = undefined;
  }, [task.id]);

  useEffect(() => {
    const wasEvaluating = prevIsEvaluatingRef.current;
    prevIsEvaluatingRef.current = isEvaluating;

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      if (evaluatedAt != null) {
        lastChoreographedEvaluatedAtRef.current = evaluatedAt;
      }
      return;
    }

    const isSuccessfulCompletion = wasEvaluating
      && !isEvaluating
      && evaluatedAt != null
      && !evaluationError
      && evaluatedAt !== lastChoreographedEvaluatedAtRef.current;

    if (!isSuccessfulCompletion) {
      return;
    }

    lastChoreographedEvaluatedAtRef.current = evaluatedAt;
    if (isAiCollapsed && isMountedRef.current) {
      setIsAiCollapsed(false);
    }
  }, [
    isEvaluating,
    evaluatedAt,
    evaluationError,
    isAiCollapsed,
  ]);

  const hasObjectives = hasApplicableObjectives(task?.analysis?.dimensions);

  const moduleColorName = getColorForModule(module);

  // Responsive widths based on screen size and panel states
  const { width: windowWidth } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 1440;
  const isMediumScreen = windowWidth >= 1200;

  // Computed panel width - narrower on smaller desktops to give editor room
  const leftPanelWidth = isLeftPanelCollapsed
    ? 56
    : showAiDrawer
      ? isLargeScreen
        ? 340
        : isMediumScreen
          ? 300
          : 260
      : isLargeScreen
        ? 420
        : isMediumScreen
          ? 360
          : 320;

  const aiDrawerWidth = isAiCollapsed ? 48 : isLargeScreen ? 360 : isMediumScreen ? 320 : 300;

  return (
    <Box flex={1} bg="$bgApp" testID={testID} overflow="hidden">
      <VStack flex={1}>
        <HStack flex={1}>
          {/* Left Panel: Context & Instructions */}
          <Box w={leftPanelWidth} borderRightWidth={1} borderColor="$borderLight" bg="$bgApp">
            {/* Sidebar Context Header */}
            <Box
              h={isLeftPanelCollapsed ? '100%' : 72}
              borderBottomWidth={isLeftPanelCollapsed ? 0 : 1}
              borderColor="$borderLight"
              bg="$bgApp"
              px={isLeftPanelCollapsed ? '$0' : '$6'}
              justifyContent="center"
            >
              {isLeftPanelCollapsed ? (
                <VStack flex={1} py="$4" alignItems="center" space="md">
                  <Pressable
                    onPress={() => navigateToModuleScreen(navigation, courseId, moduleId)}
                    p="$2"
                    borderRadius="$md"
                    sx={{ ':hover': { bg: '$bgHover' } }}
                  >
                    <Icon as={ArrowLeft} color="$textBody" size="xl" />
                  </Pressable>

                  <Pressable
                    onPress={() => setIsLeftPanelCollapsed(false)}
                    hitSlop={8}
                    p="$2"
                    borderRadius="$md"
                    sx={{ ':hover': { bg: '$bgHover' } }}
                  >
                    <Icon as={PanelLeft} color="$textSubtle" size="lg" />
                  </Pressable>
                </VStack>
              ) : (
                <HStack alignItems="center" space="md">
                  <Pressable
                    onPress={() => navigateToModuleScreen(navigation, courseId, moduleId)}
                    p="$2"
                    borderRadius="$md"
                    sx={{ ':hover': { bg: '$bgHover' } }}
                  >
                    <Icon as={ArrowLeft} color="$textBody" size="lg" />
                  </Pressable>

                  <VStack flex={1}>
                    <Text size="xs" color="$textHint" textTransform="uppercase" letterSpacing="$md" numberOfLines={1}>
                      {module?.title || 'MODULE'}
                    </Text>
                    <Heading testID="task-title" size="sm" color="$textHeading" numberOfLines={2}>
                      {task?.title}
                    </Heading>
                  </VStack>

                  <Pressable
                    onPress={() => setIsLeftPanelCollapsed(true)}
                    hitSlop={8}
                    p="$2"
                    borderRadius="$md"
                    sx={{ ':hover': { bg: '$bgHover' } }}
                  >
                    <Icon as={PanelLeftClose} color="$textSubtle" size="lg" />
                  </Pressable>
                </HStack>
              )}
            </Box>

            {/* Scrollable content - hidden when collapsed */}
            {!isLeftPanelCollapsed && (
              <ScrollView
                showsVerticalScrollIndicator={false}
                // eslint-disable-next-line react-native/no-inline-styles
                contentContainerStyle={{
                  paddingVertical: 32,
                  paddingHorizontal: 24,
                }}
              >
                <VStack space="3xl">
                  {/* Instructions Section */}
                  <VStack space="md">
                    <Text size="xs" fontWeight="bold" color="$textHint" textTransform="uppercase" letterSpacing="$md">
                      Instructions
                    </Text>
                    <TaskDescription description={task?.description} variant="compact" />
                  </VStack>

                  {/* Objectives Section */}
                  {hasObjectives && (
                    <VStack space="md">
                      <Text
                        size="xs"
                        fontWeight="bold"
                        color="$textHint"
                        textTransform="uppercase"
                        letterSpacing="$md"
                      >
                        Objectives
                      </Text>
                      <TaskObjectives objectives={task?.analysis?.dimensions} color={moduleColorName} variant="compact" />
                    </VStack>
                  )}

                  {/* Resources Section */}
                  {resources && resources.length > 0 && (
                    <VStack space="md">
                      <Text
                        size="xs"
                        fontWeight="bold"
                        color="$textHint"
                        textTransform="uppercase"
                        letterSpacing="$md"
                      >
                        Resources
                      </Text>
                      <ResourceSidebar
                        testID="resource-sidebar"
                        resources={resources}
                        thumbnails={thumbnails}
                        isCompact
                      />
                    </VStack>
                  )}

                  {/* Requirements Section */}
                  <VStack space="md">
                    <Text
                      size="xs"
                      fontWeight="bold"
                      color="$textHint"
                      textTransform="uppercase"
                      letterSpacing="$md"
                    >
                      {t('student.module.task-sidebar.title', 'Formal Requirements')}
                    </Text>
                    <TaskRequirements
                      task={task}
                      tasks={tasks}
                      hideCompletionPolicy
                      hideBorder
                      variant="compact"
                    />
                  </VStack>
                </VStack>
              </ScrollView>
            )}
          </Box>

          {/* Right Panel: The Editor Surface */}
          <Box flex={1} bg="$bgApp">
            {/* Editor Surface - Adaptive padding for breathing room */}
            <Box
              flex={1}
              bg="$bgCard"
              overflow="hidden"
              px={isLargeScreen ? '$10' : isMediumScreen ? '$8' : '$6'}
              pt={isMediumScreen ? '$8' : '$6'}
              pb="$4"
            >
              <Box flex={1} maxWidth={800} alignSelf="center" w="100%">
                <ResponseComposer
                  key={task?.id}
                  task={task}
                  readonly={readonly}
                  isMobileView={false}
                  isLoading={isLoading}
                  taskScope={taskScope}
                  safetyFuseConflict={safetyFuseConflict}
                  onOpeningPageChange={setIsAiCollapsed}
                />
              </Box>
            </Box>
          </Box>

          {/* AI Tutor Side Drawer - shown by default when enabled */}
          {hasAiTutor && (
            <View w={aiDrawerWidth} h="100%" zIndex={100}>
              <VStack flex={1} bg="$bgCard" borderLeftWidth={1} borderColor="$borderLight">
                {/* Assistance Drawer content */}
                <AssistanceDrawer
                  task={task}
                  taskScope={taskScope}
                  readonly={readonly}
                  isCollapsed={isAiCollapsed}
                  onToggleCollapse={() => setIsAiCollapsed(!isAiCollapsed)}
                  submitAction={submit}
                  cancelEvaluationAction={cancelEvaluation}
                  retryEvaluationAction={evaluate}
                />
              </VStack>
            </View>
          )}
        </HStack>
      </VStack>
    </Box>
  );
};

const TaskScreenWebLayout: React.FC<TaskScreenWebLayoutProps> = (props) => {
  const navigation = useNavigation<NativeStackNavigationProp<StudentRouteParamList>>();
  const { module, tasks } = useSelectedModule();
  const {
    task, resources, testID, courseId, moduleId,
  } = props;
  if (task.type === 'oral_exam') {
    return (
      <OralExamPrepWebLayout
        task={task}
        resources={resources}
        module={module!}
        tasks={tasks ?? []}
        testID={testID}
        courseId={courseId}
        moduleId={moduleId}
        onBack={() => navigateToModuleScreen(navigation, courseId, moduleId)}
      />
    );
  }

  return <TaskScreenWebEssayLayout {...props} />;
};

export default TaskScreenWebLayout;
